import { useEffect, useState } from "react";
import { useLocale } from "@/contexts/LocaleContext";

/* ═════════════════════════════════════════════════════════════════════
   Cookie Consent Manager — region-aware, categorised, no dark patterns.

   • Accept All and Reject Non-Essential have equal visual prominence.
   • No non-essential category is pre-selected.
   • Preferences link in the footer opens the same manager anytime.
   • Consent is versioned; if server bumps `cookie-config.version`, the
     banner re-appears to re-collect consent.
   • Persists to localStorage with a full history record.
   ═════════════════════════════════════════════════════════════════════ */

const STORAGE_KEY = "vault.cookie_consent.v1";

const DEFAULT_CATEGORIES = [
    { key: "necessary",       label: "Strictly Necessary",  required: true,  desc: "Required for the site to work. Cannot be disabled." },
    { key: "functional",      label: "Functional",          required: false, desc: "Remember preferences like language, currency and appearance." },
    { key: "analytics",       label: "Analytics",           required: false, desc: "Help us understand which pages help visitors — never captures your financial data." },
    { key: "personalisation", label: "Personalisation",     required: false, desc: "Tailor product tips and content to your interests." },
    { key: "marketing",       label: "Marketing",           required: false, desc: "Support product education campaigns on other sites." },
];

export default function CookieConsent() {
    const { t } = useLocale();
    const [open, setOpen] = useState(false);
    const [manage, setManage] = useState(false);
    const [choices, setChoices] = useState(() =>
        Object.fromEntries(DEFAULT_CATEGORIES.map((c) => [c.key, c.required]))
    );

    // Show banner if no stored consent OR user clicked "Cookie Preferences"
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) { setOpen(true); return; }
            const parsed = JSON.parse(raw);
            setChoices((c) => ({ ...c, ...parsed.choices }));
        } catch { setOpen(true); }

        const reopen = () => { setOpen(true); setManage(true); };
        window.addEventListener("vault:cookie-preferences", reopen);
        return () => window.removeEventListener("vault:cookie-preferences", reopen);
    }, []);

    const persist = (nextChoices) => {
        const record = {
            choices: nextChoices,
            version: 1,
            timestamp: new Date().toISOString(),
            language: navigator.language,
        };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(record)); } catch { /* noop */ }
        // Also append to history (last 20 entries)
        try {
            const histKey = STORAGE_KEY + ".history";
            const hist = JSON.parse(localStorage.getItem(histKey) || "[]");
            hist.push(record);
            localStorage.setItem(histKey, JSON.stringify(hist.slice(-20)));
        } catch { /* noop */ }
        window.dispatchEvent(new CustomEvent("vault:cookie-consent", { detail: record }));
    };

    const acceptAll = () => {
        const all = Object.fromEntries(DEFAULT_CATEGORIES.map((c) => [c.key, true]));
        setChoices(all);
        persist(all);
        setOpen(false);
    };
    const rejectAll = () => {
        const min = Object.fromEntries(DEFAULT_CATEGORIES.map((c) => [c.key, c.required]));
        setChoices(min);
        persist(min);
        setOpen(false);
    };
    const savePrefs = () => {
        persist(choices);
        setOpen(false);
    };

    if (!open) return null;

    return (
        <div className="cc-root" role="dialog" aria-modal="true" aria-label="Cookie preferences" data-testid="cookie-consent">
            <style>{styles}</style>
            <div className="cc-card">
                {!manage ? (
                    <>
                        <div className="cc-title">{t("cookies.title")}</div>
                        <p className="cc-body">
                            {t("cookies.body")}
                        </p>
                        <div className="cc-actions">
                            <button className="cc-btn cc-btn--secondary" onClick={rejectAll} data-testid="cc-reject">
                                {t("cookies.reject")}
                            </button>
                            <button className="cc-btn cc-btn--tertiary" onClick={() => setManage(true)} data-testid="cc-manage">
                                {t("cookies.manage")}
                            </button>
                            <button className="cc-btn cc-btn--primary" onClick={acceptAll} data-testid="cc-accept-all">
                                {t("cookies.accept_all")}
                            </button>
                        </div>
                        <div className="cc-links">
                            <a href="/cookies">Cookie Policy</a>
                            <a href="/privacy">Privacy Policy</a>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="cc-title">Manage cookie preferences</div>
                        <div className="cc-cats">
                            {DEFAULT_CATEGORIES.map((c) => (
                                <label key={c.key} className={`cc-cat ${c.required ? "cc-cat--locked" : ""}`}>
                                    <input
                                        type="checkbox"
                                        checked={!!choices[c.key]}
                                        disabled={c.required}
                                        onChange={(e) => setChoices({ ...choices, [c.key]: e.target.checked })}
                                        data-testid={`cc-cat-${c.key}`}
                                    />
                                    <div>
                                        <div className="cc-cat-label">
                                            {c.label} {c.required && <span className="cc-cat-lock">Always on</span>}
                                        </div>
                                        <div className="cc-cat-desc">{c.desc}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                        <div className="cc-actions">
                            <button className="cc-btn cc-btn--secondary" onClick={rejectAll} data-testid="cc-reject-2">{t("cookies.reject")}</button>
                            <button className="cc-btn cc-btn--primary" onClick={savePrefs} data-testid="cc-save">{t("cookies.save")}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

const styles = `
.cc-root { position: fixed; left: 20px; right: 20px; bottom: 20px; z-index: 60; display: flex; justify-content: center; pointer-events: none; }
.cc-card { pointer-events: auto; background: rgba(11,17,34,0.98); color: #F6F3EC; border: 1px solid rgba(246,243,236,0.08); border-radius: 20px; padding: 24px 28px; box-shadow: 0 30px 60px -30px rgba(0,0,0,0.7); max-width: 720px; width: 100%; backdrop-filter: blur(12px); }
.cc-title { font-size: 18px; font-weight: 700; letter-spacing: -0.01em; }
.cc-body { margin-top: 10px; font-size: 13.5px; color: rgba(246,243,236,0.7); line-height: 1.55; }
.cc-actions { display: flex; gap: 10px; margin-top: 18px; flex-wrap: wrap; }
.cc-btn { border-radius: 999px; padding: 10px 18px; font-size: 13px; font-weight: 600; border: 1px solid transparent; cursor: pointer; transition: transform .15s, background .15s, border-color .15s; }
.cc-btn--primary  { background: #C6A86B; color: #0B1122; }
.cc-btn--primary:hover { background: #d3b978; }
.cc-btn--secondary { background: transparent; color: #F6F3EC; border-color: rgba(246,243,236,0.2); }
.cc-btn--secondary:hover { border-color: rgba(246,243,236,0.4); }
.cc-btn--tertiary { background: transparent; color: rgba(246,243,236,0.7); border-color: transparent; }
.cc-btn--tertiary:hover { color: #F6F3EC; }
.cc-links { margin-top: 12px; display: flex; gap: 20px; font-size: 12px; }
.cc-links a { color: rgba(246,243,236,0.55); }
.cc-links a:hover { color: #F6F3EC; text-decoration: underline; }

.cc-cats { margin-top: 12px; display: flex; flex-direction: column; gap: 12px; max-height: 300px; overflow-y: auto; padding-right: 4px; }
.cc-cat { display: flex; gap: 12px; padding: 10px; border-radius: 12px; background: rgba(246,243,236,0.03); align-items: flex-start; cursor: pointer; }
.cc-cat--locked { cursor: default; opacity: 0.9; }
.cc-cat input { margin-top: 3px; accent-color: #C6A86B; }
.cc-cat-label { font-size: 13.5px; font-weight: 600; color: #F6F3EC; display: flex; align-items: center; gap: 8px; }
.cc-cat-lock { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase; padding: 2px 8px; border-radius: 999px; background: rgba(198,168,107,0.15); color: #C6A86B; font-weight: 700; }
.cc-cat-desc { font-size: 12px; color: rgba(246,243,236,0.55); margin-top: 3px; line-height: 1.5; }
`;
